import { DiscountInfoDTO, SubscriptionDTO } from 'api-models'
import { getCurrencySymbol } from './index'

export const formatUsageSpend = (value = 0): string =>
	`$${value.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})}`

/**
 * Display helpers for the Billing page (DEV-2383).
 *
 * The API returns marketing plan names ("ClickGUARD Standard", "[Custom]
 * ClickGUARD Pro"); Figma spells them "Standard Plan" / "Custom Pro Plan".
 * Period suffixes there also read "/ quarter" rather than the "/quarterly"
 * used at checkout, so these live apart from the shared `formatInterval*`
 * helpers instead of changing them out from under the other pages.
 */

const PLAN_DISPLAY_NAMES: Record<string, string> = {
	lite: 'Lite Plan',
	custom_lite: 'Custom Lite Plan',
	standard: 'Standard Plan',
	standard_basic: 'Standard Plan',
	legacy: 'Standard Plan',
	custom_standard: 'Custom Standard Plan',
	pro: 'Pro Plan',
	standard_pro: 'Pro Plan',
	custom_pro: 'Custom Pro Plan',
	pro_plus: 'Pro+ Plan',
	standard_pro_plus: 'Pro+ Plan'
}

/** "ClickGUARD Standard" (tier `standard`) -> "Standard Plan". */
export const getPlanDisplayName = (
	tier?: string | null,
	fallbackName?: string | null
): string => {
	if (tier && PLAN_DISPLAY_NAMES[tier]) return PLAN_DISPLAY_NAMES[tier]
	// Unmapped tier: strip the marketing prefixes so it at least reads cleanly.
	const stripped = (fallbackName || tier || '')
		.replace(/^\[[^\]]+\]\s*/, '')
		.replace(/^ClickGUARD\s+/i, '')
		.trim()
	if (!stripped) return 'Plan'
	return /plan$/i.test(stripped) ? stripped : `${stripped} Plan`
}

/** The billing period a price is charged over: "month" | "quarter" | "year". */
export const getBillingPeriod = (
	interval?: string | null,
	intervalCount?: number | null
): string => {
	if (interval === 'year') return 'year'
	if (interval === 'month' && intervalCount === 3) return 'quarter'
	if (interval === 'month') return 'month'
	return interval || 'month'
}

/** Hero price suffix, spaced: "$399.00 / month". */
export const getBillingPeriodSuffix = (
	interval?: string | null,
	intervalCount?: number | null
): string => (interval ? ` / ${getBillingPeriod(interval, intervalCount)}` : '')

/** Inline price suffix, unspaced: "$955/month" (upcoming-changes panel). */
export const getCompactPeriodSuffix = (
	interval?: string | null,
	intervalCount?: number | null
): string => (interval ? `/${getBillingPeriod(interval, intervalCount)}` : '')

/** Applies a Stripe discount to a price, staying in the source unit (cents). */
export const applyDiscount = (
	price?: number | null,
	discount?: DiscountInfoDTO | null
): number => {
	if (!price) return 0
	if (!discount) return price
	if (discount.amount_off) return Math.max(price - discount.amount_off, 0)
	if (discount.percent_off) return price * (1 - discount.percent_off / 100)
	return price
}

/** "for the first 3 months" — the gray line under a discounted price. */
export const getDiscountDurationLabel = (
	discount?: DiscountInfoDTO | null
): string | null => {
	if (!discount) return null
	if (discount.duration === 'forever') return 'forever'
	if (discount.duration === 'once') return 'for the first month'
	const months = discount.months || 0
	if (!months) return null
	return `for the first ${months} month${months === 1 ? '' : 's'}`
}

export type BillingStatusBadge = { label: string; color: 'warning' | 'error' }

/**
 * Figma always spells the label `Canceled`, never "Cancelling" — only the tone
 * changes: yellow while the user still has access, red once it has ended.
 */
export const getBillingStatusBadge = (
	subscription?: SubscriptionDTO | null
): BillingStatusBadge | null => {
	if (subscription?.status === 'CANCELLING')
		return { label: 'Canceled', color: 'warning' }
	if (subscription?.status === 'CANCELLED')
		return { label: 'Canceled', color: 'error' }
	return null
}

/** Trial length in days, read off the trial plan's interval. */
export const getTrialLengthInDays = (
	subscription?: SubscriptionDTO | null
): number | null => {
	const plan = subscription?.plan
	if (!plan) return null
	if (plan.interval === 'day') return plan.interval_count || null
	if (plan.interval === 'week') return (plan.interval_count || 1) * 7
	if (plan.interval === 'month') return (plan.interval_count || 1) * 30
	return null
}

/** `$399.00` — hero prices keep two decimals even on round amounts. */
export const formatPlanPrice = (
	priceInCents?: number | null,
	currency?: string | null
): string =>
	`${getCurrencySymbol(currency)}${((priceInCents || 0) / 100).toLocaleString(
		undefined,
		{ minimumFractionDigits: 2, maximumFractionDigits: 2 }
	)}`
